import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  IdCard, Plus, Trash2, Upload, FileText, ExternalLink, Loader2, Clock,
  ShieldCheck, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { uploadToCloudinary } from "@/lib/cloudinary";
import useDropImage from "@/hooks/useDropImage";

// Catálogo de plataformas. Las marcadas con `disponible:false` aparecen como
// "Próximamente" — preparado para crecer (UCAGECI, E-Coordina, etc.)
const PLATAFORMAS = [
  {
    slug: "carrefour",
    label: "Carrefour",
    descripcion: "Tarjeta de Personal Autorizado para acceso a centros Carrefour",
    color: "from-blue-600 to-blue-800",
    icon: "🛒",
    disponible: true,
  },
  {
    slug: "ucageci",
    label: "UCAGECI",
    descripcion: "Coordinación de actividades empresariales — UCAGECI",
    color: "from-violet-600 to-violet-800",
    icon: "📋",
    disponible: false,
  },
  {
    slug: "e-coordina",
    label: "E-Coordina",
    descripcion: "Plataforma de CAE E-Coordina",
    color: "from-emerald-600 to-emerald-800",
    icon: "🤝",
    disponible: false,
  },
  {
    slug: "ctaima",
    label: "CTAIMA",
    descripcion: "CTAIMA CAE/Logbook",
    color: "from-rose-600 to-rose-800",
    icon: "🧾",
    disponible: false,
  },
  {
    slug: "otra",
    label: "Otra plataforma",
    descripcion: "Cualquier otra acreditación que necesites llevar siempre encima",
    color: "from-zinc-600 to-zinc-800",
    icon: "🪪",
    disponible: true,
  },
];

export default function MisAcreditaciones() {
  const [data, setData] = useState({ user_name: "", foto_url: null, acreditaciones: [] });
  const [loading, setLoading] = useState(true);
  const [openAdd, setOpenAdd] = useState(null); // plataforma slug
  const [form, setForm] = useState({ label: "", codigo: "", vigencia_hasta: "" });
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null); // acreditación en lightbox
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/users/me/acreditaciones");
      setData(r.data || {});
    } catch (e) {
      toast.error("Error cargando acreditaciones");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startAdd = (plat) => {
    if (!plat.disponible) {
      toast.info(`${plat.label}: muy pronto disponible. Pendiente de integrar con la plataforma.`);
      return;
    }
    setForm({ label: plat.label, codigo: "", vigencia_hasta: "" });
    setOpenAdd(plat);
  };

  const submitAdd = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error("Selecciona la tarjeta (PDF o imagen)");
      return;
    }
    if (file.size > 15_000_000) {
      toast.error("Archivo demasiado grande (máx 15MB)");
      return;
    }
    setUploading(true);
    try {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      const resourceType = ["jpg", "jpeg", "png", "gif", "webp"].includes(ext) ? "image" : "auto";
      const up = await uploadToCloudinary(file, "dnf/users/acreditaciones", resourceType);
      await api.post("/users/me/acreditaciones", {
        plataforma: openAdd.slug,
        label: form.label || openAdd.label,
        archivo_url: up.secure_url,
        archivo_public_id: up.public_id,
        tipo_archivo: file.type || (resourceType === "image" ? "image/*" : "application/octet-stream"),
        codigo: form.codigo || null,
        vigencia_hasta: form.vigencia_hasta || null,
      });
      toast.success(`Acreditación ${openAdd.label} añadida`);
      setOpenAdd(null);
      load();
    } catch (err) {
      toast.error("Error subiendo: " + (err?.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const deleteAcr = async (acr) => {
    if (!window.confirm(`¿Borrar la acreditación ${acr.label}?`)) return;
    try {
      await api.delete(`/users/me/acreditaciones/${acr.id}`);
      toast.success("Eliminada");
      load();
    } catch (e) {
      toast.error("Error eliminando");
    }
  };

  const isImage = (a) => (a.tipo_archivo || "").startsWith("image/")
    || /\.(jpe?g|png|gif|webp)$/i.test(a.archivo_url || "");

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto" data-testid="mis-acreditaciones-page">
      <PageHeader
        title="Mis acreditaciones"
        subtitle="Tus tarjetas y credenciales — listas en cualquier acceso"
        icon={IdCard}
      />

      {/* Cabecera con foto + nombre (formato carnet, listo para enseñar al guardia) */}
      <div className="bg-gradient-to-br from-amber-500/20 via-amber-600/10 to-transparent border border-amber-500/40 rounded-2xl p-5 mb-6"
           data-testid="header-credencial">
        <div className="flex items-center gap-4">
          {data.foto_url ? (
            <img src={data.foto_url} alt={data.user_name}
              className="w-20 h-20 rounded-2xl object-cover border-2 border-amber-400 shadow-lg" />
          ) : (
            <div className="w-20 h-20 rounded-2xl bg-amber-500/30 border-2 border-amber-400 flex items-center justify-center text-amber-100 text-3xl font-bold shadow-lg">
              {(data.user_name || "?")[0]?.toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-amber-300/80">Personal autorizado</div>
            <div className="text-xl font-bold text-amber-100 truncate">{data.user_name || "—"}</div>
            <div className="text-xs text-zinc-300">RotulaciónDNF / Sierra Norte S.L.</div>
            {!data.foto_url && (
              <a href="/perfil" className="text-[11px] text-cyan-300 hover:underline inline-flex items-center gap-1 mt-1">
                ⚠️ Añade tu foto en Mi perfil <ChevronRight className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Acreditaciones activas */}
      <div className="mb-6">
        <div className="text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-3 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-400" /> Tus acreditaciones ({data.acreditaciones?.length || 0})
        </div>
        {loading ? (
          <div className="text-center py-8 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Cargando…</div>
        ) : (data.acreditaciones || []).length === 0 ? (
          <div className="text-center py-6 text-xs text-zinc-500 italic border-2 border-dashed border-zinc-800 rounded-lg">
            Aún no has añadido ninguna acreditación. Elige una plataforma abajo.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {data.acreditaciones.map((acr) => {
              const plat = PLATAFORMAS.find((p) => p.slug === acr.plataforma) || PLATAFORMAS[PLATAFORMAS.length - 1];
              return (
                <div key={acr.id} data-testid={`acreditacion-${acr.id}`}
                     className={`bg-gradient-to-br ${plat.color} rounded-xl p-4 shadow-lg relative overflow-hidden`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{plat.icon}</span>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-white/70">{plat.label}</div>
                        <div className="text-sm font-bold text-white">{acr.label}</div>
                      </div>
                    </div>
                    <button onClick={() => deleteAcr(acr)} title="Eliminar"
                      data-testid={`del-acr-${acr.id}`}
                      className="text-white/50 hover:text-rose-300 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {acr.codigo && (
                    <div className="mt-1 text-[11px] text-white/80 font-mono">
                      Código: <b className="text-white">{acr.codigo}</b>
                    </div>
                  )}
                  {acr.vigencia_hasta && (
                    <div className="text-[10px] text-white/60 flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3" /> Hasta {acr.vigencia_hasta}
                    </div>
                  )}
                  <button
                    onClick={() => setViewer(acr)}
                    data-testid={`open-acr-${acr.id}`}
                    className="mt-3 w-full bg-white/95 hover:bg-white text-zinc-900 text-sm font-bold py-2.5 rounded-lg flex items-center justify-center gap-2 active:scale-95 transition-transform">
                    {isImage(acr) ? "👁️ Mostrar al guardia" : "📄 Abrir tarjeta"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Catálogo de plataformas para añadir */}
      <div>
        <div className="text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-3 flex items-center gap-2">
          <Plus className="w-4 h-4 text-amber-400" /> Añadir nueva acreditación
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {PLATAFORMAS.map((p) => (
            <button key={p.slug} onClick={() => startAdd(p)}
              data-testid={`add-plataforma-${p.slug}`}
              className={`text-left bg-zinc-900/60 border rounded-lg p-3 transition-colors flex items-center gap-3 ${
                p.disponible
                  ? "border-zinc-800 hover:border-amber-500/40 hover:bg-zinc-900"
                  : "border-zinc-900 opacity-60"
              }`}>
              <span className="text-2xl flex-shrink-0">{p.icon}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
                  {p.label}
                  {!p.disponible && (
                    <span className="text-[9px] uppercase tracking-wider bg-zinc-800 text-zinc-500 px-1.5 py-0.5 rounded">
                      Próximamente
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-zinc-500 truncate">{p.descripcion}</div>
              </div>
              {p.disponible && <Plus className="w-4 h-4 text-amber-400 flex-shrink-0" />}
            </button>
          ))}
        </div>
      </div>

      {/* Dialog añadir */}
      <Dialog open={!!openAdd} onOpenChange={(o) => !o && setOpenAdd(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="dialog-add-acr">
          <DialogHeader>
            <DialogTitle className="text-amber-300 flex items-center gap-2">
              {openAdd?.icon} Añadir acreditación {openAdd?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-zinc-400">Nombre que verás en la tarjeta</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder={`Ej: ${openAdd?.label || "Mi acreditación"}`}
                data-testid="form-acr-label"
                className="bg-zinc-900 border-zinc-800" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-zinc-400">Código personal (opcional)</Label>
                <Input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                  placeholder="Ej: 79100"
                  data-testid="form-acr-codigo"
                  className="bg-zinc-900 border-zinc-800" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Vigente hasta (opcional)</Label>
                <Input type="date" value={form.vigencia_hasta} onChange={(e) => setForm({ ...form, vigencia_hasta: e.target.value })}
                  data-testid="form-acr-vigencia"
                  className="bg-zinc-900 border-zinc-800" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Archivo de la tarjeta (PDF o imagen)</Label>
              <input ref={fileRef} type="file" accept="image/*,application/pdf"
                data-testid="form-acr-file"
                className="block w-full text-xs text-zinc-300 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-amber-500 file:text-zinc-950 file:font-semibold file:cursor-pointer" />
              <div className="text-[10px] text-zinc-500 mt-1">Máx 15MB. JPG, PNG o PDF.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenAdd(null)} disabled={uploading}>Cancelar</Button>
            <Button onClick={submitAdd} disabled={uploading}
              data-testid="form-acr-submit"
              className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold">
              {uploading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Subiendo…</> :
                <><Upload className="w-4 h-4 mr-1.5" /> Guardar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Viewer fullscreen (la "tarjeta abierta para enseñar al guardia") */}
      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl p-2" data-testid="viewer-acreditacion">
          <DialogHeader className="px-3 pt-2 pb-1">
            <DialogTitle className="text-amber-300 text-base">
              {viewer?.label} {viewer?.codigo && <span className="text-zinc-400 text-xs font-mono ml-2">· {viewer.codigo}</span>}
            </DialogTitle>
          </DialogHeader>
          {viewer && (isImage(viewer) ? (
            <div className="flex items-center justify-center">
              <img src={viewer.archivo_url} alt={viewer.label}
                data-testid="viewer-img"
                className="max-h-[75vh] w-auto object-contain rounded" />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <FileText className="w-16 h-16 text-amber-400" />
              <div className="text-zinc-300">Tarjeta en formato PDF</div>
              <a href={viewer.archivo_url} target="_blank" rel="noreferrer"
                data-testid="viewer-pdf-open"
                className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold px-5 py-2.5 rounded-lg flex items-center gap-2">
                Abrir PDF <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          ))}
        </DialogContent>
      </Dialog>
    </div>
  );
}
